import { EventEmitter } from './events/EventEmitter';
import { PaymentError, PaymentErrorCode } from './types';
import type {
  CheckoutConfig,
  CheckoutState,
  CheckoutSuccessData,
  CustomizationParams,
  SubmitFormOptions,
  FormSubmittingData,
  FormSubmissionErrorData,
  FormReadyData,
  FormValidationData,
  AddonsChangedData,
  CouponAppliedData,
  CouponErrorData,
  FieldValueData,
  PrefillAddress,
  PrefillableFieldName,
} from './types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s\-().]{7,20}$/;

/**
 * Main PaymentCheckout class for handling checkout display
 */
export class PaymentCheckout extends EventEmitter {
  private config: CheckoutConfig;
  private state: CheckoutState;
  private iframe: HTMLIFrameElement | null = null;
  private container: HTMLElement | null = null;
  private pendingFieldRequests: Map<string, { resolve: (v: any) => void; reject: (e: any) => void }> = new Map();

  constructor(config: CheckoutConfig) {
    super();
    this.config = this.validateAndMergeConfig(config);
    this.state = this.getInitialState();
  }

  static create(config: CheckoutConfig): PaymentCheckout {
    return new PaymentCheckout(config);
  }

  /**
   * Create PaymentCheckout from an existing DOM element
   */
  static fromElement(
    element: HTMLElement,
    config: CheckoutConfig
  ): PaymentCheckout {
    const checkout = new PaymentCheckout(config);
    checkout.attachToElement(element);
    return checkout;
  }

  getState(): CheckoutState {
    return { ...this.state };
  }

  updateConfig(newConfig: Partial<CheckoutConfig>): void {
    this.config = this.validateAndMergeConfig({ ...this.config, ...newConfig });
    if (this.iframe) {
      this.iframe.src = this.getCheckoutUrl();
    }
  }

  updateTheme(newTheme: CustomizationParams): void {
    this.config = this.validateAndMergeConfig({
      ...this.config,
      theme: newTheme,
    });
    if (this.iframe) {
      this.iframe.src = this.getCheckoutUrl();
    }
  }

  init(): Promise<void> {
    if (this.state.isOpen) {
      return Promise.resolve();
    }

    this.setState({ isOpen: true, isLoading: true });
    this.emit('checkout:opened');

    return this.initializeCheckout()
      .then(() => {
        this.setState({ isLoading: false });
        this.emit('checkout:loaded');
      })
      .catch(error => {
        console.log('error', error);
        this.setState({
          isLoading: false,
          error:
            error instanceof PaymentError
              ? error
              : new PaymentError(
                  PaymentErrorCode.UNKNOWN_ERROR,
                  'Failed to open checkout',
                  error
                ),
        });
        this.emit('checkout:error', this.state.error!);
        throw error;
      });
  }

  destroy(): void {
    if (!this.state.isOpen) {
      return;
    }

    this.setState({ isOpen: false });
    this.cleanupIframe();
    this.emit('checkout:closed');
  }

  cleanup(): void {
    this.destroy();
    this.removeAllListeners();
    this.pendingFieldRequests.clear();
    this.config = {} as CheckoutConfig;
    this.state = this.getInitialState();
  }

  attachToElement(element: HTMLElement): void {
    this.container = element;
  }

  submitForm(options: SubmitFormOptions = {}): void {
    if (!this.iframe) {
      throw new PaymentError(
        PaymentErrorCode.INVALID_CONFIG,
        'Checkout iframe is not initialized'
      );
    }

    const message = {
      type: 'SUBMIT_FORM',
      timestamp: Date.now(),
      data: {
        paymentMethod: options.paymentMethod || 'card',
        forceValidation: options.forceValidation !== false,
      },
    };
    console.log('submitForm', message);
    this.sendSecureMessage(this.iframe, 'SUBMIT_FORM', message.data);
  }

  isFormReady(): boolean {
    return this.state.isOpen && !this.state.isLoading;
  }

  addAddon(addonId: string): void {
    if (!this.iframe) {
      console.warn('Checkout iframe is not initialized');
      return;
    }
    this.sendSecureMessage(this.iframe, 'ADD_ADDON', { addonId });
  }

  removeAddon(addonId: string): void {
    if (!this.iframe) {
      console.warn('Checkout iframe is not initialized');
      return;
    }
    this.sendSecureMessage(this.iframe, 'REMOVE_ADDON', { addonId });
  }

  toggleAddon(addonId: string): void {
    if (!this.iframe) {
      console.warn('Checkout iframe is not initialized');
      return;
    }
    this.sendSecureMessage(this.iframe, 'TOGGLE_ADDON', { addonId });
  }

  applyCoupon(code: string): void {
    if (!code || code.trim().length === 0) {
      throw new PaymentError(
        PaymentErrorCode.FIELD_VALIDATION_ERROR,
        'Coupon code cannot be empty'
      );
    }
    if (!this.iframe) {
      console.warn('Checkout iframe is not initialized');
      return;
    }
    this.sendSecureMessage(this.iframe, 'APPLY_COUPON', { code });
  }

  removeCoupon(): void {
    if (!this.iframe) {
      console.warn('Checkout iframe is not initialized');
      return;
    }
    this.sendSecureMessage(this.iframe, 'REMOVE_COUPON', {});
  }

  // --- Field getter/setter methods ---

  async setEmail(value: string): Promise<void> {
    if (!EMAIL_REGEX.test(value)) {
      throw new PaymentError(PaymentErrorCode.FIELD_VALIDATION_ERROR, `Invalid email format: "${value}"`);
    }
    return this.setField('email', value);
  }

  async getEmail(): Promise<string | null> {
    return this.getField('email') as Promise<string | null>;
  }

  async setFirstName(value: string): Promise<void> {
    if (!value || value.trim().length === 0) {
      throw new PaymentError(PaymentErrorCode.FIELD_VALIDATION_ERROR, 'First name cannot be empty');
    }
    return this.setField('first_name', value);
  }

  async getFirstName(): Promise<string | null> {
    return this.getField('first_name') as Promise<string | null>;
  }

  async setLastName(value: string): Promise<void> {
    if (!value || value.trim().length === 0) {
      throw new PaymentError(PaymentErrorCode.FIELD_VALIDATION_ERROR, 'Last name cannot be empty');
    }
    return this.setField('last_name', value);
  }

  async getLastName(): Promise<string | null> {
    return this.getField('last_name') as Promise<string | null>;
  }

  async setPhone(value: string): Promise<void> {
    if (!PHONE_REGEX.test(value)) {
      throw new PaymentError(PaymentErrorCode.FIELD_VALIDATION_ERROR, `Invalid phone format: "${value}"`);
    }
    return this.setField('phone', value);
  }

  async getPhone(): Promise<string | null> {
    return this.getField('phone') as Promise<string | null>;
  }

  async setAddress(value: PrefillAddress): Promise<void> {
    if (this.config.theme?.fields?.address?.hide !== true) {
      throw new PaymentError(
        PaymentErrorCode.FIELD_NOT_SETTABLE,
        'setAddress() can only be used when fields.address.hide is true'
      );
    }
    if (!value || typeof value !== 'object') {
      throw new PaymentError(PaymentErrorCode.FIELD_VALIDATION_ERROR, 'Address must be an object');
    }
    return this.setField('address', value);
  }

  async getAddress(): Promise<PrefillAddress | null> {
    return this.getField('address') as Promise<PrefillAddress | null>;
  }

  // --- Private methods ---

  private async setField(field: string, value: string | PrefillAddress): Promise<void> {
    if (!this.iframe) {
      throw new PaymentError(PaymentErrorCode.IFRAME_NOT_READY, 'Checkout iframe is not initialized');
    }
    this.sendSecureMessage(this.iframe, 'SET_FIELD', { field, value });
  }

  private getField(field: string): Promise<string | PrefillAddress | null> {
    if (!this.iframe) {
      return Promise.reject(new PaymentError(PaymentErrorCode.IFRAME_NOT_READY, 'Checkout iframe is not initialized'));
    }

    const requestId = `${field}_${Date.now()}`;
    this.sendSecureMessage(this.iframe, 'GET_FIELD', { field, requestId });

    return new Promise((resolve, reject) => {
      this.pendingFieldRequests.set(requestId, { resolve, reject });
      setTimeout(() => {
        if (this.pendingFieldRequests.has(requestId)) {
          this.pendingFieldRequests.delete(requestId);
          reject(new PaymentError(PaymentErrorCode.UNKNOWN_ERROR, `Timeout waiting for field value: ${field}`));
        }
      }, 5000);
    });
  }

  private validateAndMergeConfig(config: CheckoutConfig): CheckoutConfig {
    if (!config.creatorId) {
      throw new PaymentError(PaymentErrorCode.CREATOR_ID_REQUIRED, 'Creator ID is required');
    }
    if (!config.productId) {
      throw new PaymentError(PaymentErrorCode.PRODUCT_ID_REQUIRED, 'Product ID is required');
    }
    if (!config.checkoutSessionSecret) {
      throw new PaymentError(PaymentErrorCode.CHECKOUT_SESSION_SECRET_REQUIRED, 'Checkout session secret is required');
    }

    const merged = { environment: 'sandbox' as const, ...config };

    if (merged.theme) {
      this.emitPrefillFieldWarnings(merged.theme);
    }

    return merged;
  }

  private emitPrefillFieldWarnings(theme: CustomizationParams): void {
    const { prefill, fields, billing_display_fields } = theme;

    if (billing_display_fields && fields) {
      console.warn(
        '[FanBasis SDK] billing_display_fields is deprecated in favor of the fields config. ' +
        'Please migrate to the fields config for per-field visibility and disable controls. ' +
        'When both are provided, fields takes precedence.'
      );
    }

    if (!fields) return;

    const fieldNames: PrefillableFieldName[] = ['email', 'first_name', 'last_name', 'phone', 'address'];

    for (const fieldName of fieldNames) {
      const control = fields[fieldName];
      if (!control) continue;

      const hasPrefill = fieldName === 'address'
        ? prefill?.address && Object.keys(prefill.address).length > 0
        : !!(prefill as any)?.[fieldName];

      if (control.hide && !hasPrefill) {
        console.warn(
          `[FanBasis SDK] Field "${fieldName}" is hidden but has no prefill value. ` +
          'The field value will be missing from the checkout payload.'
        );
      }
      if (control.disable && !hasPrefill) {
        console.warn(
          `[FanBasis SDK] Field "${fieldName}" is disabled but has no prefill value. ` +
          'The customer will see an empty locked field.'
        );
      }
    }
  }

  private getInitialState(): CheckoutState {
    return {
      isOpen: false,
      isLoading: false,
      error: null,
      isInitialized: false,
    };
  }

  private setState(updates: Partial<CheckoutState>): void {
    this.state = { ...this.state, ...updates };
  }

  private async initializeCheckout(): Promise<void> {
    if (this.container) {
      await this.createEmbeddedCheckout();
    }
  }

  private async createEmbeddedCheckout(): Promise<void> {
    if (!this.container) {
      throw new PaymentError(
        PaymentErrorCode.INVALID_CONFIG,
        'Container element is required for embedded checkout'
      );
    }

    this.iframe = document.createElement('iframe');
    this.iframe.src = this.getCheckoutUrl();
    this.iframe.allow = 'payment';
    this.iframe.style.border = 'none';
    if (this.config.containerOptions?.width) {
      this.iframe.style.width = this.config.containerOptions.width;
    }
    if (this.config.containerOptions?.height) {
      this.iframe.style.height = this.config.containerOptions.height;
    }
    this.iframe.sandbox.add(
      'allow-scripts',
      'allow-same-origin',
      'allow-forms',
      'allow-popups',
      'allow-modals',
      'allow-downloads',
      'allow-top-navigation-by-user-activation'
    );

    this.container.appendChild(this.iframe);

    window.addEventListener('message', this.handleMessage.bind(this));
  }

  private getBaseUrl(): string {
    if (this.config.overrideBaseUrl) {
      return this.config.overrideBaseUrl;
    }

    return this.config.environment === 'sandbox'
      ? 'https://embedded-checkout.qa.dev-fan-basis.com/'
      : 'https://embedded.fanbasis.io/';
  }

  private getCheckoutUrl(): string {
    const baseUrl =
      this.getBaseUrl() +
      'session/' +
      this.config.creatorId +
      '/' +
      this.config.productId +
      '/' +
      this.config.checkoutSessionSecret;

    const params = new URLSearchParams({});

    if (this.config.showSubmitButton !== undefined) {
      params.append(
        'show_submit_button',
        this.config.showSubmitButton.toString()
      );
    }

    if (this.config.theme) {
      params.append('theme', this.config.theme.theme);
      params.append(
        'show_product_info',
        this.config.theme.show_product_info.toString()
      );
      params.append('product_layout', this.config.theme.product_layout);
      params.append(
        'show_coupon_row',
        (this.config.theme.show_coupon_row ?? false).toString()
      );
      params.append('accent_color', this.config.theme.accent_color);
      if (this.config.theme.coupon_row_disclaimer !== undefined) {
        params.append(
          'coupon_row_disclaimer',
          this.config.theme.coupon_row_disclaimer
        );
      }

      if (this.config.theme.show_headings !== undefined) {
        params.append(
          'show_headings',
          this.config.theme.show_headings.toString()
        );
      }
      if (this.config.theme.show_powered_by !== undefined) {
        params.append(
          'show_powered_by',
          this.config.theme.show_powered_by.toString()
        );
      }

      if (this.config.theme.background_color) {
        params.append('background_color', this.config.theme.background_color);
      }
      if (this.config.theme.label_color) {
        params.append('label_color', this.config.theme.label_color);
      }
      if (this.config.theme.input_background_color) {
        params.append(
          'input_background_color',
          this.config.theme.input_background_color
        );
      }
      if (this.config.theme.product_text_color) {
        params.append(
          'product_text_color',
          this.config.theme.product_text_color
        );
      }
      if (this.config.theme.heading_color) {
        params.append('heading_color', this.config.theme.heading_color);
      }
      if (this.config.theme.secondary_color) {
        params.append('secondary_color', this.config.theme.secondary_color);
      }
      if (this.config.theme.border_color) {
        params.append('border_color', this.config.theme.border_color);
      }
      if (this.config.theme.surface_color) {
        params.append('surface_color', this.config.theme.surface_color);
      }
      if (this.config.theme.product_image) {
        params.append('product_image', this.config.theme.product_image);
      }
      if (this.config.theme.billing_display_fields) {
        params.append(
          'billing_display_fields',
          this.config.theme.billing_display_fields
        );
      }
      if (this.config.theme.billing_form_placement) {
        params.append(
          'billing_form_placement',
          this.config.theme.billing_form_placement
        );
      }

      if (this.config.theme.prefill) {
        params.append('prefill', JSON.stringify(this.config.theme.prefill));
      }
      if (this.config.theme.fields) {
        params.append('fields', JSON.stringify(this.config.theme.fields));
      }
    }

    if (this.config.redirectSettings?.success_redirect_url) {
      params.append(
        'redirect_url',
        this.config.redirectSettings.success_redirect_url
      );
    }

    if (this.config.bumpProductIds) {
      params.append('addonIds', this.config.bumpProductIds.join(','));
    }
    if (this.config.couponCode) {
      params.append('coupon_code', this.config.couponCode);
    }
    if (this.config.affiliateCode) {
      params.append('affiliate_code', this.config.affiliateCode);
    }

    if (this.config.showAllAddons) {
      params.append('show_all_addons', 'true');
    }

    if (this.config.collectPhone) {
      params.append('collect_phone', 'true');
    }

    if (this.config.metadata) {
      params.append('metadata', JSON.stringify(this.config.metadata));
    }

    return `${baseUrl}?${params.toString()}`;
  }

  private handleMessage(event: MessageEvent): void {
    const { type, data } = event.data || {};
    if (!type) return;

    switch (type) {
      case 'PING':
        this.sendSecureMessage(this.iframe!, 'PONG', {});
        break;
      case 'PAYMENT_SUCCESS':
        this.emit('checkout:success', data as CheckoutSuccessData);
        this.handleRedirect('success');
        break;
      case 'PAYMENT_ERROR':
        this.emit('checkout:error', data);
        this.handleRedirect('failure');
        break;
      case 'checkout:ready':
        this.setState({ isLoading: false });
        this.emit('checkout:loaded');
        break;
      case 'checkout:close':
        this.destroy();
        break;
      case 'FORM_READY':
        this.emit('form:ready', data as FormReadyData);
        break;
      case 'FORM_SUBMITTING':
        this.emit('form:submitting', data as FormSubmittingData);
        break;
      case 'FORM_SUBMISSION_ERROR':
        this.emit('form:submission_error', data as FormSubmissionErrorData);
        break;
      case 'FORM_VALIDATION':
        this.emit('form:validation', data as FormValidationData);
        break;
      case 'ADDONS_CHANGED':
        this.emit('addons:changed', data as AddonsChangedData);
        break;
      case 'COUPON_APPLIED':
        this.emit('coupon:applied', data as CouponAppliedData);
        break;
      case 'COUPON_ERROR':
        this.emit('coupon:error', data as CouponErrorData);
        break;
      case 'GET_FIELD_RESPONSE': {
        const { requestId, field, value } = data as { requestId: string; field: string; value: any };
        const pending = this.pendingFieldRequests.get(requestId);
        if (pending) {
          this.pendingFieldRequests.delete(requestId);
          pending.resolve(value);
        }
        this.emit('field:value', { field, value } as FieldValueData);
        break;
      }
    }
  }

  private sendSecureMessage(
    iframe: HTMLIFrameElement,
    type: string,
    data: any
  ): void {
    iframe.contentWindow?.postMessage(
      { type, data, timestamp: Date.now() },
      '*'
    );
  }

  private handleRedirect(type: 'success' | 'failure'): void {
    const redirectSettings = this.config.redirectSettings;

    if (!redirectSettings || !redirectSettings.always_redirect) {
      return;
    }

    let redirectUrl: string | undefined;

    if (type === 'success' && redirectSettings.success_redirect_url) {
      redirectUrl = redirectSettings.success_redirect_url;
    } else if (type === 'failure' && redirectSettings.failure_redirect_url) {
      redirectUrl = redirectSettings.failure_redirect_url;
    }

    if (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl!;
      }, 100);
    }
  }

  private cleanupIframe(): void {
    if (this.iframe && this.container) {
      this.container.removeChild(this.iframe);
      this.iframe = null;
    }

    window.removeEventListener('message', this.handleMessage.bind(this));
  }
}
